import { useHandler } from "@redotech/react-util/hook";
import { WeightChangeSource } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import type { CarrierAndService } from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { FulfillmentOrderLineItem } from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { FulfillmentGroupActions } from "@redotech/redo-model/outbound-labels/fulfillment-group-actions";
import {
  OutboundParcel,
  Parcel,
} from "@redotech/redo-model/outbound-labels/parcel";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { ParcelDetails } from "../../common/parcels/parcel-details";
import type { DisplayParcel } from "../../utils/use-group-info";

export const ParcelDetailsCard = memo(function ParcelDetailsCard({
  allowMultipleParcels,
  displayedButtonAction,
  everythingDisabled,
  isEditingLocked,
  mixedParcels,
  mixedWeight,
  onParcelAdd,
  onParcelChange,
  onRemoveParcel,
  parcelValues,
  selectedParcels,
  selectedRate,
  selectedStatus,
  trackingNumbers,
  usedPackageLearning,
  usedWeightLearning,
  showParcelPackingSlipButtons,
  fulfillmentGroupId,
  items,
  onAllocationsFlush,
}: {
  allowMultipleParcels: boolean;
  displayedButtonAction: FulfillmentGroupActions;
  everythingDisabled: boolean;
  hideDivider?: boolean;
  isEditingLocked: boolean;
  mixedParcels: boolean;
  mixedWeight: boolean;
  onParcelAdd: (parcel: OutboundParcel) => void;
  onParcelChange: (
    parcel: Partial<OutboundParcel>,
    index: number,
    source?: WeightChangeSource,
  ) => void;
  onRemoveParcel: (index: number) => void;
  parcelValues: readonly Parcel[];
  selectedParcels: DisplayParcel[];
  selectedRate?: CarrierAndService | null;
  selectedStatus: FulfillmentGroupStatus | undefined | null;
  trackingNumbers: string[] | undefined;
  usedPackageLearning: boolean;
  usedWeightLearning: boolean;
  showParcelPackingSlipButtons?: boolean;
  fulfillmentGroupId?: string;
  items: FulfillmentOrderLineItem[];
  onAllocationsFlush?: (
    allocations: Map<
      number,
      { externalId: string; sku: string; quantity: number }[]
    >,
  ) => void;
}) {
  const [parcelModalOpen, setParcelModalOpen] = useState(false);
  const packageCount = selectedParcels?.length || 1;

  const handleAddPackage = useHandler(() => {
    setParcelModalOpen(true);
  });

  const handleCloseModal = useHandler(() => {
    setParcelModalOpen(false);
  });

  return (
    <Flex bgColor="primary" dir="column" flexDirection="column" w="full">
      <Flex justify="space-between">
        <Flex flexDirection="column" justify="center">
          <Text color="secondary" fontSize="sm" fontWeight="medium">
            Packages ({packageCount})
          </Text>
        </Flex>
        {allowMultipleParcels && (
          <RedoButton
            disabled={isEditingLocked}
            hierarchy="tertiary"
            onClick={handleAddPackage}
            size="sm"
            text="Add another package"
          />
        )}
      </Flex>

      <ParcelDetails
        displayedButtonAction={displayedButtonAction}
        everythingDisabled={everythingDisabled}
        fulfillmentGroupId={fulfillmentGroupId}
        isEditingLocked={isEditingLocked}
        items={items}
        mixedParcels={mixedParcels}
        mixedWeight={mixedWeight}
        onAllocationsFlush={onAllocationsFlush}
        onParcelAdd={onParcelAdd}
        onParcelChange={onParcelChange}
        onParcelModalClose={handleCloseModal}
        onParcelModalOpen={handleAddPackage}
        onRemoveParcel={onRemoveParcel}
        parcelModalOpen={parcelModalOpen}
        parcelValues={parcelValues}
        selectedParcels={selectedParcels}
        selectedRate={selectedRate}
        selectedStatus={selectedStatus}
        showParcelPackingSlipButtons={showParcelPackingSlipButtons}
        trackingNumbers={trackingNumbers}
        usedPackageLearning={usedPackageLearning}
        usedWeightLearning={usedWeightLearning}
      />
    </Flex>
  );
});
